import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setTheme, Theme } from '../../store/slices/themeSlice';

const ThemeToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const { theme, resolvedTheme } = useAppSelector((state) => state.theme);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleThemeChange = (newTheme: Theme) => {
    dispatch(setTheme(newTheme));
    setShowMenu(false);
  };

  const cycleTheme = () => {
    const themes: Theme[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    handleThemeChange(themes[nextIndex]);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const getThemeIcon = () => {
    if (theme === 'system') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }
    if (resolvedTheme === 'dark') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowMenu(!showMenu)}
        className="p-2 rounded-lg text-[var(--color-header-link-primary)] hover:bg-[var(--color-neutral-100)] transition-colors"
        aria-label={`Current theme: ${theme}. Click to open theme menu.`}
        title={`Theme: ${theme} (${resolvedTheme})`}
      >
        {getThemeIcon()}
      </button>
      
      {/* Dropdown menu for direct theme selection */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 w-32 bg-[var(--color-default-background)] rounded-lg shadow-lg border border-[var(--color-neutral-border)] z-50"
        >
          <div className="py-1">
            <button
              onClick={() => handleThemeChange('light')}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-neutral-100)] transition-colors ${
                theme === 'light' ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-700)]' : 'text-[var(--color-default-font)]'
              }`}
            >
              ☀️ Light
            </button>
            <button
              onClick={() => handleThemeChange('dark')}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-neutral-100)] transition-colors ${
                theme === 'dark' ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-700)]' : 'text-[var(--color-default-font)]'
              }`}
            >
              🌙 Dark
            </button>
            <button
              onClick={() => handleThemeChange('system')}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-neutral-100)] transition-colors ${
                theme === 'system' ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-700)]' : 'text-[var(--color-default-font)]'
              }`}
            >
              💻 System
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
